import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  Loader2,
  CreditCard,
  ExternalLink,
  Crown,
  Calendar,
  GraduationCap,
  ShieldCheck,
  KeyRound,
  User as UserIcon,
  Send,
  SlidersHorizontal,
} from "lucide-react";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { getStripeEnvironment } from "@/lib/stripe";
import { useSubscription } from "@/hooks/useSubscription";
import { modules, TIER_LABEL, type AcademyTier } from "@/lib/academy";
import { useLessonProgress } from "@/hooks/useLessonProgress";
import { Skeleton } from "@/components/ui/skeleton";
import { TelegramConnect } from "@/components/notifications/TelegramConnect";
import { AlertPreferences } from "@/components/notifications/AlertPreferences";
import { MemberAvatar } from "@/components/dashboard/MemberAvatar";
import { getTelegramStatus, type TelegramStatus } from "@/lib/telegram.functions";
import { getAlertPreferences } from "@/lib/push.functions";

export const Route = createFileRoute("/_authenticated/dashboard/profile")({
  head: () => ({
    meta: [
      { title: "My Account | XAU Pro Academy" },
      { name: "description", content: "Manage your XAU Pro Academy account and profile details." },
    ],
  }),
  component: ProfilePage,
});

function StatusChip({
  icon,
  label,
  value,
  active,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  active: boolean | null;
}) {
  return (
    <div className="flex items-center gap-2 rounded-full border border-border/60 bg-background/40 px-3 py-1.5">
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          active === null
            ? "bg-muted-foreground/40"
            : active
              ? "bg-emerald shadow-[0_0_6px_var(--emerald)]"
              : "bg-muted-foreground/40"
        }`}
      />
      <span className="text-muted-foreground">{icon}</span>
      <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </span>
      <span className="text-[11px] font-medium text-foreground">{value}</span>
    </div>
  );
}

function ZoneCard({
  label,
  icon,
  title,
  description,
  children,
}: {
  label: string;
  icon: React.ReactNode;
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="relative overflow-hidden rounded-2xl border border-border/60 bg-card/40 p-6 backdrop-blur-sm">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
      <div className="flex items-center gap-2">
        <span className="text-gold">{icon}</span>
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          {label}
        </p>
      </div>
      <h2 className="mt-2 font-display text-lg font-semibold">{title}</h2>
      {description && (
        <p className="mt-1 text-xs text-muted-foreground">{description}</p>
      )}
      <div className="mt-5">{children}</div>
    </section>
  );
}

function ProfilePage() {
  const { user, profile, refreshProfile } = useAuth();
  const { subscription, isActive } = useSubscription();
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);

  // Command header live status
  const fetchTelegramStatus = useServerFn(getTelegramStatus);
  const fetchAlertPrefs = useServerFn(getAlertPreferences);
  const [telegramStatus, setTelegramStatus] = useState<TelegramStatus | null>(null);
  const [alertScope, setAlertScope] = useState<{ scope: string; paused: boolean } | null>(null);

  useEffect(() => {
    setName(profile?.display_name ?? "");
  }, [profile]);

  useEffect(() => {
    fetchTelegramStatus().then(setTelegramStatus).catch(() => setTelegramStatus(null));
    fetchAlertPrefs()
      .then((p) => setAlertScope({ scope: p.scope, paused: p.paused }))
      .catch(() => setAlertScope(null));
  }, [fetchTelegramStatus, fetchAlertPrefs]);

  const tier = (profile?.tier ?? "free") as AcademyTier;
  const isPaid = tier !== "free";
  const isElite = tier === "elite";
  const isLifetime = subscription?.price_id === "elite_lifetime";

  const periodEndDate = subscription?.current_period_end
    ? new Date(subscription.current_period_end)
    : null;
  const renewsLabel = (() => {
    if (!isActive || !subscription) return null;
    if (isLifetime) return "Lifetime access, never expires";
    if (!periodEndDate) return null;
    const formatted = periodEndDate.toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
    if (subscription.cancel_at_period_end || subscription.status === "canceled") {
      return `Access ends on ${formatted}`;
    }
    return `Renews on ${formatted}`;
  })();

  const memberSince = useMemo(() => {
    const raw = user?.created_at;
    if (!raw) return null;
    return new Date(raw).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
    });
  }, [user?.created_at]);

  // Learning progress across all modules / lessons
  const { completed, loading: progressLoading } = useLessonProgress();
  const allLessons = useMemo(() => modules.flatMap((m) => m.lessons), []);
  const totalLessons = allLessons.length;
  const doneLessons = allLessons.filter((l) => completed.has(l.slug)).length;
  const masteryPct = totalLessons === 0 ? 0 : Math.round((doneLessons / totalLessons) * 100);
  const masteryLevel = (() => {
    if (masteryPct === 0) return "Initiate";
    if (masteryPct < 25) return "Apprentice";
    if (masteryPct < 50) return "Practitioner";
    if (masteryPct < 75) return "Strategist";
    if (masteryPct < 100) return "Expert";
    return "Grandmaster";
  })();

  const initials = (() => {
    const source = profile?.display_name || user?.email || "";
    return source
      .split(/[\s@.]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase() ?? "")
      .join("") || "X";
  })();

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({ display_name: name })
      .eq("id", user.id);
    setSaving(false);
    if (error) return toast.error(error.message);
    await refreshProfile();
    toast.success("Profile updated");
  };

  const handleManageBilling = async () => {
    setOpeningPortal(true);
    const { data, error } = await supabase.functions.invoke("create-portal-session", {
      body: {
        environment: getStripeEnvironment(),
        returnUrl: `${window.location.origin}/dashboard/profile`,
      },
    });
    setOpeningPortal(false);
    if (error || !data?.url) {
      toast.error(error?.message || "Could not open billing portal");
      return;
    }
    window.open(data.url, "_blank");
  };

  const handleChangePassword = async () => {
    if (!user?.email) return;
    setSendingReset(true);
    const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setSendingReset(false);
    if (error) return toast.error(error.message);
    toast.success("Password reset link sent to your email");
  };

  const alertChipValue = alertScope
    ? alertScope.paused
      ? "Paused"
      : alertScope.scope === "all"
        ? "All setups"
        : alertScope.scope === "intraday"
          ? "Intraday"
          : "Swing"
    : "…";

  return (
    <div className="container mx-auto max-w-6xl px-4 py-10">
      <div>
        <div className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold-muted px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">
          <UserIcon className="h-3 w-3" /> Account Command Station
        </div>
        <h1 className="mt-3 font-display text-3xl font-bold md:text-4xl">
          Your <span className="text-gradient-gold">Profile</span>
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Every aspect of your account, membership, and alert channels in one view.
        </p>
      </div>

      {/* Command Header Strip */}
      <div
        className={`mt-8 relative overflow-hidden rounded-2xl border bg-gradient-to-br from-gold-muted/10 via-card/30 to-card/60 p-6 backdrop-blur-md md:p-8 ${
          isElite
            ? "border-gold/60 shadow-gold ring-1 ring-gold/40"
            : "border-border/60"
        }`}
      >
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/50 to-transparent" />
        {isElite && (
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-gold/10 via-transparent to-gold/5" />
        )}
        <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-5">
            <MemberAvatar
              src={profile?.avatar_url}
              initials={initials}
              name={profile?.display_name || user?.email?.split("@")[0] || "Trader"}
              className={`h-20 w-20 rounded-full font-display text-2xl font-bold ${
                isElite
                  ? "bg-gradient-gold text-gold-foreground shadow-gold"
                  : "bg-gold-muted text-gold ring-1 ring-gold/30"
              }`}
            />
            <div>
              <div className="text-base font-medium text-foreground">
                {profile?.display_name || user?.email?.split("@")[0] || "Trader"}
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
                    isElite
                      ? "bg-gradient-gold text-gold-foreground shadow-gold"
                      : isPaid
                        ? "bg-gold-muted text-gold ring-1 ring-gold/40"
                        : "bg-muted text-muted-foreground"
                  }`}
                >
                  <Crown className="h-3.5 w-3.5" />
                  {TIER_LABEL[tier]} Member
                </span>
                {memberSince && (
                  <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Calendar className="h-3.5 w-3.5" />
                    Member since {memberSince}
                  </span>
                )}
              </div>
            </div>
          </div>

          {!isPaid && (
            <div className="flex flex-col items-start gap-4 lg:items-end">
              <Button asChild variant="gold" size="lg">
                <Link to="/pricing">Upgrade Membership</Link>
              </Button>
            </div>
          )}

        </div>

        {/* Live status chips */}
        <div className="relative mt-6 flex flex-wrap gap-2 border-t border-border/40 pt-5">
          <StatusChip
            icon={<Crown className="h-3 w-3" />}
            label="Plan"
            value={isPaid ? `${TIER_LABEL[tier]}${renewsLabel ? "" : ""}` : "Free"}
            active={isPaid}
          />
          <StatusChip
            icon={<Send className="h-3 w-3" />}
            label="Telegram"
            value={
              telegramStatus === null
                ? "…"
                : telegramStatus.linked
                  ? `Linked${telegramStatus.username ? ` @${telegramStatus.username}` : ""}`
                  : "Not linked"
            }
            active={telegramStatus === null ? null : telegramStatus.linked}
          />
          <StatusChip
            icon={<SlidersHorizontal className="h-3 w-3" />}
            label="Alerts"
            value={alertChipValue}
            active={alertScope === null ? null : !alertScope.paused}
          />
        </div>
      </div>

      {/* Control Deck: four zones */}
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        {/* Zone 1: Identity & Security */}
        <ZoneCard
          label="Zone 01 · Identity"
          icon={<ShieldCheck className="h-4 w-4" />}
          title="Identity & Security"
          description="How you appear across the platform, and how your account stays protected."
        >
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={user?.email ?? ""} disabled />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Display name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
              />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button type="submit" variant="gold" disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleChangePassword}
                disabled={sendingReset || !user?.email}
              >
                {sendingReset ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <KeyRound className="h-4 w-4" /> Change Password
                  </>
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Password changes are handled through a secure link sent to your email.
            </p>
          </form>
        </ZoneCard>

        {/* Zone 2: Membership & Billing */}
        <ZoneCard
          label="Zone 02 · Membership"
          icon={<CreditCard className="h-4 w-4" />}
          title="Membership & Billing"
        >
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            Current plan:
            <Badge variant="outline" className="capitalize">
              {tier}
            </Badge>
          </div>
          {renewsLabel && (
            <p className="mt-2 text-xs text-muted-foreground">{renewsLabel}</p>
          )}

          <div className="mt-5 flex flex-col gap-3 sm:flex-row">
            {isPaid && !isLifetime ? (
              <Button
                variant="outline"
                onClick={handleManageBilling}
                disabled={openingPortal}
              >
                {openingPortal ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    Manage billing <ExternalLink className="h-4 w-4" />
                  </>
                )}
              </Button>
            ) : null}
            <Button asChild variant="ghost">
              <Link to="/pricing">View all plans</Link>
            </Button>
          </div>


          {isPaid && !isLifetime && (
            <p className="mt-4 text-xs text-muted-foreground">
              Manage billing opens the secure billing portal in a new tab where you can update payment method, change plan, or cancel. Cancellations keep your access until the end of your billing period.
            </p>
          )}
          {isLifetime && (
            <p className="mt-4 text-xs text-muted-foreground">
              You have lifetime access, no recurring charges, nothing to manage. Reach out at support@xauproacademy.com if you need help.
            </p>
          )}
          {!isPaid && (
            <p className="mt-4 text-xs text-muted-foreground">
              Paid members receive every A and A+ setup with full levels on Telegram and in the XAU Terminal™, plus the complete academy and member reports.
            </p>
          )}
        </ZoneCard>

        {/* Zone 3: Alert Channels */}
        <ZoneCard
          label="Zone 03 · Channels"
          icon={<Send className="h-4 w-4" />}
          title="Alert Channels"
          description="Where trade alerts reach you. Telegram delivers every alert by private message."
        >
          <div className="space-y-4">
            <TelegramConnect />
          </div>
        </ZoneCard>

        {/* Zone 4: Alert Scope */}
        <ZoneCard
          label="Zone 04 · Scope"
          icon={<SlidersHorizontal className="h-4 w-4" />}
          title="Alert Scope"
          description="Which setups notify you, and a master pause when you step away."
        >
          <AlertPreferences />
        </ZoneCard>
      </div>

      {/* Learning Progress: full-width footer strip */}
      <div className="mt-6 rounded-2xl border border-border/60 bg-card/40 p-6 backdrop-blur-sm">
        <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <GraduationCap className="h-5 w-5 text-gold" />
            <div>
              <h2 className="font-display text-lg font-semibold">Learning Progress</h2>
              <p className="text-xs text-muted-foreground">
                Across all {totalLessons} lessons in the XAU Pro Academy.
              </p>
            </div>
          </div>
          <Button asChild variant="ghost">
            <Link to="/dashboard/academy">Continue learning →</Link>
          </Button>
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-3">
          <div className="rounded-xl border border-border/60 bg-background/40 p-4">
            <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              <GraduationCap className="h-3 w-3 text-gold" />
              Lessons
            </div>
            <div className="mt-2 font-display text-2xl font-bold">
              {progressLoading ? (
                <Skeleton className="h-7 w-20" />
              ) : (
                <>
                  <span className="text-gradient-gold">{doneLessons}</span>
                  <span className="text-muted-foreground"> / {totalLessons}</span>
                </>
              )}
            </div>
            <div className="mt-1 text-[11px] text-muted-foreground">Completed</div>
          </div>

          <div className="rounded-xl border border-border/60 bg-background/40 p-4">
            <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              <ShieldCheck className="h-3 w-3 text-gold" />
              Mastery
            </div>
            <div className="mt-2 font-display text-2xl font-bold text-gradient-gold">
              {progressLoading ? <Skeleton className="h-7 w-24" /> : masteryLevel}
            </div>
            <div className="mt-1 text-[11px] text-muted-foreground">
              {progressLoading ? <Skeleton className="h-3 w-16" /> : `${masteryPct}% complete`}
            </div>
          </div>

          <div className="flex flex-col justify-center rounded-xl border border-border/60 bg-background/40 p-4">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              Curriculum completion
            </div>
            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-border/60">
              <div
                className="h-full rounded-full bg-gradient-gold shadow-gold transition-all"
                style={{ width: progressLoading ? "0%" : `${masteryPct}%` }}
              />
            </div>
            <div className="mt-2 font-mono text-[11px] text-muted-foreground">
              {progressLoading ? "…" : `${masteryPct}%`}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
